import React from "react"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { string, object } from "yup"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { useDestination, useUpdateDestinationWithFile } from "../../../hooks/useDestinations"
import { useSelectedProject } from "../../../contexts/SelectedProjectContext"
import { twClassNames } from "../../../helpers"
import { MAX_IMAGE_FILE_SIZE } from "../../../constants/fileHandling"
import SettingsForm from "./components/SettingsForm"
import SettingsPreview from "./components/SettingsPreview"
import type { DestinationFormValues } from "../../../constants/types"

const validationSchema = (t: TFunction) => {
  return object().shape(
    {
      nameEn: string()
        .when(["nameFi"], {
          is: (nameFi: string) => !nameFi,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      nameFi: string()
        .when(["nameEn"], {
          is: (nameEn: string) => !nameEn,
          then: () => string().required(t("common.validation.eitherRequired"))
        })
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength")),
      streetName: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      city: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      country: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength")),
      phone: string()
        .min(6, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .matches(/^\+(?:[0-9]?){6,14}[0-9]$/, t("common.validation.invalidPhone")),
      descriptionEn: string().when(["descriptionFi"], {
        is: (descriptionFi: string) => !descriptionFi,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      descriptionFi: string().when(["descriptionEn"], {
        is: (descriptionEn: string) => !descriptionEn,
        then: () => string().required(t("common.validation.eitherRequired"))
      }),
      pincode: string()
        .matches(/^\d+$/, t("common.validation.digitsOnly"))
        .min(4, t("common.validation.invalidMinLength"))
        .max(4, t("common.validation.invalidMaxLength")),
      accessCode: string()
        .min(5, t("common.validation.invalidMinLength"))
        .max(8, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required"))
        .matches(/^[A-Z0-9]+$/, t("common.validation.onlyUppercaseLettersAndNumbers"))
    },
    [
      ["nameFi", "nameEn"],
      ["descriptionFi", "descriptionEn"]
    ]
  )
}

const SettingsPage: React.FC = () => {
  const { t } = useTranslation()
  const { selectedDestinationId } = useSelectedProject()
  const { data: destination, isLoading } = useDestination(selectedDestinationId)
  const updateDestinationWithFileMutation = useUpdateDestinationWithFile()

  const formik = useFormik<DestinationFormValues>({
    initialValues: {
      isPublic: destination?.isPublic ?? false,
      pincode: destination?.pincode || "",
      accessCode: destination?.accessCode || "",
      nameEn: destination?.nameEn || "",
      nameFi: destination?.nameFi || "",
      streetName: destination?.streetName || "",
      country: destination?.country || "",
      city: destination?.city || "",
      phone: destination?.phone || "",
      descriptionEn: destination?.descriptionEn || "",
      descriptionFi: destination?.descriptionFi || "",
      image: destination?.image || null,
      displayCall: destination?.displayCall ?? false,
      displayMessage: destination?.displayMessage ?? false,
      ownerTeamId: destination?.ownerTeamId
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: validationSchema(t),
    onSubmit: (values, actions) => onSave(values, actions)
  })

  const onSave = async ({ image, ...values }: DestinationFormValues, actions: { setSubmitting: (v: boolean) => void }) => {
    const data: Record<string, unknown> = {
      isPublic: values.isPublic,
      pincode: values.pincode,
      accessCode: values.accessCode,
      name: values.nameEn || values.nameFi,
      nameEn: values.nameEn,
      nameFi: values.nameFi,
      description: values.descriptionEn || values.descriptionFi,
      descriptionEn: values.descriptionEn,
      descriptionFi: values.descriptionFi,
      city: values.city.toLowerCase(),
      streetName: values.streetName,
      phone: values.phone,
      displayCall: values.displayCall,
      displayMessage: values.displayMessage
    }

    // TODO: Split image input to separate API request to simplify backend
    if (image instanceof File) {
      if (image.size / (1024 * 1024) > MAX_IMAGE_FILE_SIZE) {
        toast.error(t("settings.notifications.image.maxSizeExceeded", { max_size: MAX_IMAGE_FILE_SIZE }))
        return
      }
      data.image = image
    } else if (image === null) {
      data.deleteImage = true
    }

    try {
      await updateDestinationWithFileMutation.mutateAsync({ id: destination?.id as string, data })
      toast.success(t("settings.notifications.save.success"))
    } catch {
      toast.error(t("settings.notifications.save.failed"))
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("settings.title")}</h1>
      <div className={twClassNames(formik.dirty ? "tw-mb-20" : "tw-mb-12", "tw-w-full")}>
        <div className="tw-mb-6 tw-shadow tw-bg-white tw-rounded">
          <div className="tw-flex tw-p-6 max-md:tw-flex-col">
            {/* Left side */}
            <div className="tw-w-full md:tw-w-1/2">
              <SettingsForm formik={formik} isLoading={isLoading} />
            </div>
            {/* Right side */}
            <div className="tw-w-full md:tw-w-1/2 tw-min-w-[360px] max-xs:tw-hidden">
              <SettingsPreview data={formik.values} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SettingsPage
